import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grow,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'

import { useCreatePropertyAdStore } from 'store'

import {
  EMPTY_OPTION,
  CATEGORIES,
  ESTATE_SUB_CATEGORIES,
  COMMERCIAL_SUB_CATEGORIES,
  LAND_SUB_CATEGORIES,
} from 'store/CreatePropertyAdSlice/constants'
import {
  Controller,
  useWatch,
  Control,
  UseFormTrigger,
  UseFormGetValues,
  UseFormResetField,
  UseFormReset,
  UseFormClearErrors,
} from 'react-hook-form'
import { FormValues, DefaultValues } from '../'

import { TransitionProps } from '@mui/material/transitions'

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>
  },
  ref: React.Ref<unknown>
) {
  return <Grow ref={ref} {...props} />
})

interface WarnDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: () => void
  selectSource: string
}
const WarnDialog = (props: WarnDialogProps) => {
  const { t } = useTranslation()
  const { open, onClose, onSubmit, selectSource } = props
  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      fullWidth
      maxWidth={'sm'}
    >
      <DialogTitle sx={{ color: 'warning.main' }}>
        {t('manageEstateAd.steps.estateCategory.modal.title')}
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
          {t(
            `manageEstateAd.steps.estateCategory.modal.${
              selectSource === 'category'
                ? 'contentCategory'
                : 'contentSubCategory'
            }`
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('manageEstateAd.steps.estateCategory.modal.cancel')}
        </Button>
        <Button variant="contained" onClick={onSubmit}>
          {t('manageEstateAd.steps.estateCategory.modal.continue')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface Props {
  control: Control<FormValues>
  trigger: UseFormTrigger<FormValues>
  getValues: UseFormGetValues<FormValues>
  resetField: UseFormResetField<FormValues>
  reset: UseFormReset<FormValues>
  clearErrors: UseFormClearErrors<FormValues>
}

const Step1 = (props: Props) => {
  const { control, getValues, resetField, reset, clearErrors } = props

  const [selectSource, setSelectSource] = React.useState<
    'category' | 'subCategory' | ''
  >('')
  const [openCategory, setOpenCategory] = React.useState(false)
  const [openSubCategory, setOpenSubCategory] = React.useState(false)
  const [showWarnDialog, setShowWarnDialog] = React.useState(false)

  const handleCloseDialog = () => {
    setOpenCategory(false)
    setShowWarnDialog(false)
  }

  const [uncompleteStep, invalidateStep, unsetStepInvalid] =
    useCreatePropertyAdStore(
      (state) => [
        state.uncompleteStep,
        state.invalidateStep,
        state.unsetStepInvalid,
      ],
      shallow
    )

  const { t } = useTranslation()

  /**
   * Updates the active category and resets the active subcategory
   * @param {React.ChangeEvent<HTMLInputElement>} event - the event that triggered the change
   */
  const handleChangeCategory = (event, field) => {
    field.onChange(event)
    resetField('step1.subCategory', { defaultValue: '' })
    reset({
      step1: getValues('step1'),
      location: getValues('location'),
      adAttributes: getValues('adAttributes'),
      property: DefaultValues.property,
      adPhotos: getValues('adPhotos'),
    })
    clearErrors(['property'])
    unsetStepInvalid('property')
    setOpenCategory(false)
  }

  const handleChangeSubCategory = (event, field) => {
    field.onChange(event)
    reset({
      step1: getValues('step1'),
      location: getValues('location'),
      adAttributes: getValues('adAttributes'),
      property: DefaultValues.property,
      adPhotos: getValues('adPhotos'),
    })
    clearErrors(['property'])
    unsetStepInvalid('property')
  }

  const watchCategory = useWatch({
    control,
    name: 'step1.category',
  })

  const watchSubCategory = useWatch({
    control,
    name: 'step1.subCategory',
  })

  React.useEffect(() => {
    if (!watchCategory) {
      uncompleteStep('category')
      invalidateStep('category')
    }
  }, [
    getValues,
    invalidateStep,
    reset,
    resetField,
    uncompleteStep,
    watchCategory,
  ])

  React.useEffect(() => {
    if (!watchSubCategory) {
      uncompleteStep('category')
      invalidateStep('category')
    }
  }, [invalidateStep, resetField, uncompleteStep, watchSubCategory])

  const IS_ESTATE = watchCategory === 'estate'
  const IS_COMMERCIAL = watchCategory === 'commercial'
  const IS_LAND = watchCategory === 'land'

  const IS_LAND_FALLBACK = IS_LAND ? LAND_SUB_CATEGORIES : []

  const IS_COM_OR_LAND_FALLBACK = IS_COMMERCIAL
    ? COMMERCIAL_SUB_CATEGORIES
    : IS_LAND_FALLBACK

  const currentSubCategory = IS_ESTATE
    ? ESTATE_SUB_CATEGORIES
    : IS_COM_OR_LAND_FALLBACK

  const handleOpen = (name: 'category' | 'subCategory') => {
    if (name === 'category') {
      if (watchCategory && watchSubCategory) {
        setShowWarnDialog(true)
        setSelectSource('category')
      } else {
        setOpenCategory(true)
      }
    } else if (name === 'subCategory') {
      if (watchSubCategory) {
        setShowWarnDialog(true)
        setSelectSource('subCategory')
      } else {
        setOpenSubCategory(true)
      }
    }
  }

  const handleContinue = () => {
    if (selectSource === 'category') {
      setOpenCategory(true)
      setSelectSource('')
    } else if (selectSource === 'subCategory') {
      setOpenSubCategory(true)
      setSelectSource('')
    }
    setShowWarnDialog(false)
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: '20px',
          flexDirection: 'column',
        }}
      >
        <FormControl id="estateCategory" fullWidth>
          <InputLabel>
            {t('manageEstateAd.steps.estateCategory.title')}
          </InputLabel>
          <Controller
            name="step1.category"
            control={control}
            rules={{
              required: t(`manageEstateAd.fieldRequired`) as unknown as string,
            }}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <>
                <Select
                  fullWidth
                  {...field}
                  onChange={(e) => handleChangeCategory(e, field)}
                  error={Boolean(error)}
                  input={
                    <OutlinedInput
                      label={t('manageEstateAd.steps.estateCategory.title')}
                    />
                  }
                  MenuProps={{
                    sx: {
                      '& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected': {
                        bgcolor: 'primary.main',
                      },
                    },
                  }}
                  open={openCategory}
                  onOpen={() => handleOpen('category')}
                  onClose={() => setOpenCategory(false)}
                >
                  <MenuItem value={EMPTY_OPTION.value}>
                    {t(EMPTY_OPTION.label)}
                  </MenuItem>
                  {CATEGORIES.map((category) => (
                    <MenuItem key={category.label} value={category.value}>
                      {t(category.label)}
                    </MenuItem>
                  ))}
                </Select>
                <Grow in={Boolean(error)}>
                  <FormHelperText error={Boolean(error)}>
                    {error?.message}
                  </FormHelperText>
                </Grow>
              </>
            )}
          />
        </FormControl>
        <Box>
          <FormControl id="estateSubCategory" fullWidth>
            <InputLabel id="estateSubCategory">
              {t('manageEstateAd.steps.estateCategory.estateSubCategory.title')}
            </InputLabel>

            <Controller
              name="step1.subCategory"
              control={control}
              rules={{
                required: t(
                  `manageEstateAd.fieldRequired`
                ) as unknown as string,
              }}
              defaultValue={''}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Select
                    fullWidth
                    {...field}
                    error={Boolean(error)}
                    onChange={(e) => handleChangeSubCategory(e, field)}
                    input={
                      <OutlinedInput
                        label={t(
                          'manageEstateAd.steps.estateCategory.estateSubCategory.title'
                        )}
                      />
                    }
                    MenuProps={{
                      sx: {
                        '& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected': {
                          bgcolor: 'primary.main',
                        },
                      },
                    }}
                    open={openSubCategory}
                    onOpen={() => handleOpen('subCategory')}
                    onClose={() => setOpenSubCategory(false)}
                  >
                    {watchCategory === '' ? (
                      <MenuItem disabled>
                        {t(
                          'manageEstateAd.steps.estateCategory.categoryNotSelected'
                        )}
                      </MenuItem>
                    ) : null}
                    <MenuItem value={EMPTY_OPTION.value}>
                      {t(EMPTY_OPTION.label)}
                    </MenuItem>
                    {currentSubCategory.map((category) => (
                      <MenuItem key={category.label} value={category.value}>
                        {t(category.label)}
                      </MenuItem>
                    ))}
                  </Select>
                  <Grow in={Boolean(error)}>
                    <FormHelperText error={Boolean(error)}>
                      {error?.message}
                    </FormHelperText>
                  </Grow>
                </>
              )}
            />
          </FormControl>
        </Box>
        <WarnDialog
          open={showWarnDialog}
          onClose={handleCloseDialog}
          onSubmit={handleContinue}
          selectSource={selectSource}
        />
      </Box>
    </>
  )
}

export default React.memo(Step1)
